/* eslint-disable @typescript-eslint/explicit-member-accessibility */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-depth */
import Url from 'url-parse';
import { v4 as uuidv4 } from 'uuid';
import packageJson from '../package.json';
import type { BtmTeaCore as BtmTeaCoreType } from './btm-tea-core';
import { BtmTeaCore } from './btm-tea-core';
import { defaultBlockConfig } from './constants';
import ExposureCore from './exposure-core';
import RouteMonitor from './route-monitor';
import { IBizAutoLogResponse } from './type/btm-biz';
import {
  BTMEventType,
  BtmExposeInfo,
  BtmInitParams,
  BtmPageShowInfo,
  BusinessReportParams,
  EventMap,
  IActionRecord,
  IManualOpts,
  RemoteConfig,
  TeaParams,
  UrlQueryParams
} from './type/btm-core';
import {
  BIZ_ACTION_MAX_TS_GAP,
  BTM_ACTION_BTM_PRE,
  BTM_TIMESTAMP_PRE,
  EmptyBizLogResponse,
  HISTORY_MAX_LEN,
  getBtmCode,
  isByPassValidate,
  isValidBTM,
  isValidCode,
  matchBtmCode
} from './util/actionBtm';
import { getFormatPagePattern, sortObjectByKeyLength } from './util/config';
import DeferredPromise from './util/deferred-promise';
import { Logger } from './util/logger';
import {
  findNode,
  findParentWithAttribute,
  getIsProd,
  getSessionStorage,
  getUrlQueryParams,
  getValidBtmId,
  objectToQuery,
  safeStringfy,
  setSessionStorage
} from './util/pages';
import { universalGetItem, universalSetItem } from './util/storage';

const { version } = packageJson;

type EventHandler = (...args: any[]) => void;

export default class BtmCore {
  /** Tea 实例 */
  tea: BtmTeaCoreType | null = null;

  /** 区块点击曝光实例 */
  exposureCore?: ExposureCore;

  /** BTM 页面环境数据 */
  btmInfo: BtmPageShowInfo = {} as BtmPageShowInfo;

  /** 上报回调函数，上报前调用 */
  reportCallback?: () => void;

  /** 是否已经初始化 */
  isInited = false;

  /** BCM context */
  BCMContext: Record<string, unknown> = {};

  getReverseIndex: (() => Record<string, Set<string>>) | undefined;

  /** SPA URL 与 B 位映射关系 */
  private spaConfig?: Record<string, string>;

  /** 页面 URL正则 与 B 位映射关系 */
  private pagePattern?: Record<string, string>;

  /** 页面 URL正则 与 AB 位映射关系 */
  private tccPagePattern?: Record<string, string>;

  /** BTM A位 */
  private btmAId = '';

  /** BTM B位 */
  private btmBId = 'b0';

  /** BTM 传给业务方的数据 */
  private btmExposeInfo: BtmExposeInfo = {} as BtmExposeInfo;

  /** BCM 页面参数 */
  private pageParams: Record<string, any> = {};

  /** BCM 局部参数 */
  private unitParams: Record<string, any> | null = null;

  /** 曝光埋点是否已再次上报 */
  private resent = false;

  /** 业务系统最后一次被点击的DOM节点 */
  private target: HTMLElement | null = null;

  /** 路由监控实例 */
  private routeMonitor?: RouteMonitor;

  /** 是否需要上报此页面 */
  private isNeedReportPage?: () => boolean;

  /** 接入系统是否是 MPA */
  private isMPA = false;

  /** 上一次的 action record */
  private _pre_action_record: IActionRecord = {
    action_btm: '',
    ts: 0
  };

  /** biz log url 记录 */
  private _actionHistory: IActionRecord[] = [];
  private _bizUrlBtmMap: Map<string, Set<string>> | null = null; // { url: Set<btm> }

  /** 上报等待队列，防止初始化完成前调用sendBtmLog等方法 */
  private readonly reportQueue: BusinessReportParams[] = [];

  /** 路由变更事件名 */
  private readonly URL_CHANGE_EVENT_NAME: string = 'BTM_WEB_SDK-URL_CHANGE';

  private logger: Logger | null = null;

  private _region = 'sg';

  private previousTs = 0;

  private pageShowTs = 0;

  private remoteConfig: RemoteConfig = {};

  private appId = 0;

  private eventHandlers: { [K in keyof EventMap]?: EventHandler[] } = {};

  private pageInitPromise = new DeferredPromise<void>();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener(
        'click',
        (e) => {
          this.target = e.target as HTMLElement;
        },
        true
      );
    }
  }

  /**
   * BTM Web SDK 初始化
   * @param params BTM Web SDK 初始化参数
   */
  init(
    params: BtmInitParams,
    remoteConfig?: RemoteConfig,
    getReverseIndex?: () => Record<string, Set<string>>,
    appId?: number
  ) {
    try {
      this.pageShowTs = Date.now();
      if (typeof window === 'undefined') {
        console.warn('不支持非浏览器环境');
        return;
      }

      // 避免重复初始化
      if (this.isInited) {
        return;
      }

      window['btm-info'] = this;
      this.trigger('config', remoteConfig);

      this.appId = appId ?? 4068;
      this.remoteConfig = remoteConfig || {};
      this.getReverseIndex = getReverseIndex;

      const {
        btmAId,
        uniqueId,
        teaInitParams = {},
        spaConfig = {},
        pagePattern = {},
        isMPA = false,
        appName = '',
        isNeedReportPage,
        blockConfig = defaultBlockConfig,
        reportCallback,
        customReport,
        bizUrlMap,
        region,
        devLog = false
      } = params;

      this._region = region || 'sg';

      this.logger = new Logger(devLog);

      // 如果传入了appName，请求tcc获取页面规则
      // tcc规则解决了上报未注册埋点的问题，若混用，还是可能会出现ab位不匹配问题
      if (appName) {
        this.tccPagePattern = this.getTccPageRules(btmAId, remoteConfig || {}, bizUrlMap);
        this.logger.info('tccPagePattern', this.tccPagePattern, this._bizUrlBtmMap);
      }
      this.isNeedReportPage = isNeedReportPage;
      // 初始化各数据字段
      this.isInited = true;
      this.isMPA = isMPA;
      this.spaConfig = spaConfig;
      this.pagePattern = sortObjectByKeyLength(pagePattern);
      this.btmAId = btmAId || 'a0';
      this.reportCallback = reportCallback;
      // 初始化 launchId
      let launchId = getSessionStorage('btm_launch_id');
      if (!launchId) {
        launchId = `${new Date().getTime()}`;
        setSessionStorage('btm_launch_id', launchId);
      }

      this.btmInfo.btm_sdk_version = version || null;
      this.btmInfo.is_prod = getIsProd();
      this.btmInfo.launch_id = launchId;
      this.btmInfo.unique_id = uniqueId;

      if (window?.__UNIVERSAL_TRACKER_DEBUG__?.getBtmConfig) {
        window?.__UNIVERSAL_TRACKER_DEBUG__?.getBtmConfig(remoteConfig);
      }

      // 初始化 Tea
      this.tea = new BtmTeaCore(teaInitParams, customReport, devLog);

      // 情况1:SPA + 打开autoTrack => 开启路由切换监听 + 初始化上报一次
      // 情况2:SPA + 关闭autoTrack => 什么都不做，业务方通过pageInit手动上报
      // 情况3:MPA => 初始化上报一次
      if (!isMPA) {
        if (this.shouldReport(window?.location.href, true)) {
          this.pageInit();
        }
        this.initRouteMonitor();
      }
      if (isMPA) {
        this.pageInit();
      }

      this._recoverCachedPreInfo();

      while (this.reportQueue.length !== 0) {
        const reportInfo = this.reportQueue.shift();
        reportInfo && this.sendBtmLog(reportInfo.teaParams, reportInfo?.target);
      }

      this.btmBId = this.getCurrentPageBId();

      this.pageInitPromise.promise.then(() => {
        this.exposureCore = new ExposureCore(this, blockConfig, remoteConfig);
      });
      this.trigger('init', this.btmInfo);
    } catch (error) {
      console.error('Universal Tracker Sdk init error', error);
    }
  }

  /**
   * 页面初始化，初始化页面维度的参数
   * 1、初始页面参数
   * 2、处理BCM数据
   * @returns
   */
  pageInit(): void {
    try {
      if (!this.isInited) {
        console.warn('The BTM WEB SDK is not initialized. Please ensure it is initialized before use.');
        return;
      }
      if (this.isNeedReportPage && !this.isNeedReportPage()) {
        return;
      }
      // 重置 BCM 相关字段
      this.resetBCMParams();

      const timeTemp = new Date().getTime();
      const urlQuery = getUrlQueryParams(window?.location?.href);
      const { btm_ppre, btm_pre, btm_show_id: btm_pre_show_id, btm_pre_unit_params = '{}' } = urlQuery;
      const preUnitParams = JSON.parse(decodeURIComponent(btm_pre_unit_params));

      this.btmBId = this.getCurrentPageBId();

      this.btmExposeInfo = {
        btm: `${this.btmAId}.${this.btmBId}.c0.d0`,
        btm_pre: btm_pre || this.btmInfo?.btm || 'a0.b0.c0.d0',
        btm_ppre: btm_ppre || this.btmInfo?.btm_pre || 'a0.b0.c0.d0',
        btm_show_id: uuidv4(),
        btm_pre_show_id: btm_pre_show_id || this.btmInfo?.btm_show_id,
        current_url: window?.location?.href,
        btm_sdk_version: this.btmInfo.btm_sdk_version,
        is_prod: this.btmInfo.is_prod
      };

      this.btmInfo = {
        ...this.btmInfo,
        ...this.btmExposeInfo,
        enter_time: timeTemp,
        btm_pre_unit_params: preUnitParams,
        btm_page_params: this.pageParams
      };

      this._recoverCachedPreInfo();

      this.btmReport({
        eventName: BTMEventType.PageShow,
        eventParams: {
          ...this.btmInfo
        },
        time: this.pageShowTs
      });
      if (this.pageInitPromise.status === 'pending') {
        this.pageInitPromise.resolve();
      }
    } catch (error) {
      console.error('BTM WEB SDK：自动上报API调用失败。', error);
    }
  }

  /**
   * 用户手动上报 b 点位
   * 1、初始页面参数
   * 2、处理BCM数据
   * @param btmBId 用于业务方手动上报btm_page_show
   */
  manualPageInit(btmBId: string, params?: Record<string, unknown>, ts?: number, useBeacon?: boolean): void {
    try {
      if (!this.isInited) {
        console.warn('The BTM WEB SDK is not initialized. Please ensure it is initialized before use.');
        return;
      }

      const urlQuery = getUrlQueryParams(window?.location?.href);
      const { btm_ppre, btm_pre, btm_show_id: btm_pre_show_id, btm_pre_unit_params = '{}' } = urlQuery;
      const defaultPreUnitParams = JSON.parse(decodeURIComponent(btm_pre_unit_params));

      // 1. 根据 btmInfo 取 btm, btm_pre,btm_ppre, btm_show_id, btm_pre__show_id 信息
      const timeTemp = new Date().getTime();
      const {
        btm: prePageBtm, // prePageBtm 上一个页面的 btm
        btm_pre: prePageBtmPre, // prePageBtmPre 上一个页面的 btm_pre
        btm_ppre: prePageBtmPpre, // prePageBtmPre 上一个页面的 btm_ppre
        btm_show_id: prePageBtmShowId, // prePageBtmShowId 上一个页面的 btm_show_id
        btm_pre_show_id: prePageBtmPreShowId // prePageBtmShowId 上一个页面的 btm_pre_show_id
      } = this.btmInfo;

      const preUnitParams = defaultPreUnitParams ? defaultPreUnitParams : this.unitParams;

      // 2. 将环境变量进行修改 BCM信息重置, b点位更新, btmExposeInfo, btmInfo数据更新。
      this.resetBCMParams();
      this.btmBId = btmBId;
      // 通过前一次自动上报的btm信息的b位判断是不是第一次手动上报；
      // 如果是第一次手动上报，则把上一次自动上报的pre信息保留
      const isFirstManual = prePageBtm ? prePageBtm.split('.')[1] === 'b0' : false;

      this.btmExposeInfo = {
        btm: `${this.btmAId}.${this.btmBId}.c0.d0`,
        btm_pre: isFirstManual ? prePageBtmPre : btm_pre || prePageBtm || 'a0.b0.c0.d0',
        btm_ppre: isFirstManual ? prePageBtmPpre : btm_ppre || prePageBtmPre || 'a0.b0.c0.d0',
        btm_show_id: uuidv4(),
        btm_pre_show_id: isFirstManual ? prePageBtmPreShowId : btm_pre_show_id || prePageBtmShowId,
        current_url: window?.location?.href,
        btm_sdk_version: this.btmInfo.btm_sdk_version,
        is_prod: this.btmInfo.is_prod
      };

      this.btmInfo = {
        ...this.btmInfo,
        ...this.btmExposeInfo,
        enter_time: timeTemp,
        btm_pre_unit_params: preUnitParams,
        btm_page_params: { ...this.pageParams, ...params }
      };

      this._recoverCachedPreInfo();

      // 3. 进行上报
      this.btmReport({
        eventName: BTMEventType.PageShow,
        eventParams: {
          ...this.btmInfo
        },
        time: ts,
        useBeacon
      });
      if (this.pageInitPromise.status === 'pending') {
        this.pageInitPromise.resolve();
      }
    } catch (error) {
      console.error('BTM WEB SDK：手动上报API调用失败。', error);
    }
  }

  /** 设置页面参数 */
  setPageParams(pageParams: Record<string, any>): void {
    if (!this.isInited) {
      console.warn('The BTM WEB SDK is not initialized. Please ensure it is initialized before use.');
      return;
    }
    this.pageParams = pageParams;
    if (!this.resent) {
      this.resent = true;
      this.btmReport({
        eventName: BTMEventType.PageShow,
        eventParams: {
          ...this.btmInfo,
          btm_page_params: this.pageParams
        }
      });
    } else {
      console.warn('Page Param 重复设置');
    }
  }

  /** 设置需要传递的参数 */
  setUnitParams(unitParams: Record<string, any>): void {
    if (!this.isInited) {
      console.warn('The BTM WEB SDK is not initialized. Please ensure it is initialized before use.');
      return;
    }
    this.unitParams = unitParams;
  }

  /** 获取BTM页面信息 */
  getPageBtmInfo(): BtmExposeInfo {
    return {
      ...this.btmExposeInfo,
      btm: `${this.btmAId}.${this.btmBId}.c0.d0`
    };
  }

  /** 获取BTM信息 */
  getFullBtmInfo(target?: HTMLElement | null, cdInfo?: string): BtmPageShowInfo {
    try {
      const btmInfo: BtmPageShowInfo = { ...this.btmExposeInfo, ...this.btmInfo };

      // 如果用户有传入 C、D 点位，则使用用户的 C、D 点位
      if (cdInfo) {
        btmInfo.btm = `${this.btmAId}.${this.btmBId}.${cdInfo}`;
        return btmInfo;
      }

      if (target) {
        const nodeInfo = findNode(target);
        let { btmArray: cdBtmInfo } = nodeInfo;
        if (nodeInfo?.dataBtmIdParent !== '') {
          btmInfo.btm = getValidBtmId(nodeInfo?.dataBtmIdParent);
          return btmInfo;
        }
        if (cdBtmInfo.length === 0) {
          cdBtmInfo = ['d0', 'c0'];
        }
        // ['l', '1', 'c0689'] => ['1_l', 'c0689']
        if (cdBtmInfo.length > 2) {
          const { length } = cdBtmInfo;
          // 遍历出来多个d位，需要将d位置拼接
          const dArr: string[] = cdBtmInfo.slice(0, length - 1);
          const dInfo = dArr[0];
          const firstC = cdBtmInfo.find((item) => /^[c][0-9]{3,9}$/.test(item));
          cdBtmInfo = [dInfo, firstC || 'c0'];
        }
        if (cdBtmInfo.length === 2) {
          const cdBtmInfoArr = cdBtmInfo[0].split('.');
          if (cdBtmInfoArr.length === 2) {
            const [newC, newD] = cdBtmInfoArr;
            if (newC.startsWith('c')) {
              cdBtmInfo = [newD, newC];
            }
          }
        }

        const btmIds = getValidBtmId(
          [this.btmAId, this.btmBId, cdBtmInfo[1] || 'c0', cdBtmInfo[0] || 'd0'].join('.')
        );
        btmInfo.btm = btmIds;
      } else {
        btmInfo.btm = `${this.btmAId}.${this.btmBId}.c0.d0`;
      }
      return btmInfo;
    } catch (error) {
      console.error('getFullBtmInfo error ->', error);
      return { ...this.btmInfo, btm: 'a0.b0.c0.d0' };
    }
  }

  /** 补充业务埋点BTM信息 */
  sendBtmLog(teaParams: TeaParams, target: HTMLElement | null): void {
    if (!this.tea?.report) {
      this.reportQueue.push({ teaParams, target });
    } else {
      const { eventName, eventParams } = teaParams;
      const fullBtmInfo = this.getFullBtmInfo(target);
      this.btmReport({
        eventName,
        eventParams: {
          ...fullBtmInfo,
          ...eventParams
        }
      });
    }
  }

  /** 手动触发点击上报 */
  sendBtmLogManually(options: IManualOpts) {
    const { target, btm, type, btmBId, params, duration, ts, useBeacon } = options;
    if (type === 'pageShow') {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.manualPageInit(btmBId!, params, ts, useBeacon);
      return;
    }
    const finalFormatBtm = this._getFinalBtm(target || null, btm);

    let fullBtmInfo: BtmPageShowInfo;
    if (isByPassValidate(type, finalFormatBtm)) {
      fullBtmInfo = { ...this.btmExposeInfo, ...this.btmInfo };
      fullBtmInfo.btm = finalFormatBtm;
    } else {
      fullBtmInfo = this.getFullBtmInfo(null, this._formatCdInfo(finalFormatBtm));
    }

    const parentBCM = findParentWithAttribute(target);
    this.btmReport({
      eventName:
        // eslint-disable-next-line no-nested-ternary
        type === 'click'
          ? BTMEventType.Click
          : type === 'disappear'
          ? BTMEventType.ModuleDisappear
          : BTMEventType.ModuleShow,
      eventParams: {
        ...fullBtmInfo,
        btm_block_params: params
          ? { ...this.BCMContext, ...parentBCM, ...params }
          : { ...this.BCMContext, ...parentBCM },
        duration: type === 'disappear' && duration ? duration : undefined
      },
      time: ts,
      useBeacon
    });
  }

  /** 拼接BTM参数 */
  getBtmUrl(
    url: string,
    isBlank: boolean,
    target: HTMLElement | null,
    unitParams?: Record<string, any> | null
  ): string {
    this.beforeGetBtmUrl(target);
    try {
      const parseUrl = Url(url, {}, true);
      const { origin, pathname, query, hash } = parseUrl;
      const { btm } = this.getFullBtmInfo(target);

      // 判断跳转的页面是否是相同的 b 点位
      // 如果相同  btm_ppre, btm_pre, 保留 btmInfo 对应字段的值, btm_show_id 保持上次的 btm_pre_show_id
      // 如果不相同 btm_ppre, btm_pre, 取 btmInfo 之前的 btm_pre, btm 字段, btm_show_id 保持上次的 btm_show_id
      const isSamePageBId = !isBlank && this.getPageBIdByUrl(url) === this.btmBId;
      const btm_ppre = isSamePageBId ? this.btmInfo.btm_ppre : this.btmInfo.btm_pre;
      const btm_pre = isSamePageBId ? this.btmInfo.btm_pre : btm;
      const btm_pre_show_id = isSamePageBId ? this.btmInfo?.btm_pre_show_id : this.btmInfo?.btm_show_id;

      const queryObj: UrlQueryParams = {
        ...query,
        btm_ppre: btm_ppre || 'a0.b0.c0.d0',
        btm_pre: btm_pre || 'a0.b0.c0.d0',
        btm_show_id: btm_pre_show_id
      };
      if (unitParams || this.unitParams) {
        queryObj.btm_pre_unit_params = encodeURIComponent(JSON.stringify(unitParams || this.unitParams));
      }
      const newQuery = objectToQuery(queryObj);
      let newUrl = '';
      if (origin === 'null') {
        newUrl = `${pathname}?${newQuery}${hash}`;
      } else {
        newUrl = `${origin + pathname}?${newQuery}${hash}`;
      }
      return newUrl;
    } catch (error) {
      console.error('getBtmUrl error ->', error);
      return url;
    } finally {
      // bcm 参数用一次后 进行销毁
      this.unitParams = null;
    }
  }

  /** BTM跳转 */
  btmJump(url: string, target: HTMLElement | null, opts?: { isReplace?: boolean; isBlank?: boolean }): void {
    if (typeof window === 'undefined') {
      console.warn('不支持非浏览器环境');
      return;
    }
    const { isReplace = false, isBlank = false } = opts || {};
    const btmUrl = this.getBtmUrl(url, isBlank, target);
    if (isReplace) {
      window.location.replace(btmUrl);
    } else if (isBlank) {
      window.open(btmUrl);
    } else {
      window.location.href = btmUrl;
    }
  }

  /**
   * 根据 spaConfig, pagePattern 匹配当前的路径信息
   * @notes 该 api 同时暴露给外部 (如热力图，检验工具)使用
   * @param {string} url 页面链接 支持 1. 相对路径 2. 绝对路径
   * @returns {string} 返回对应的 B点位, 匹配不到则返回''字符串
   */
  getPageBIdByUrl(url: string): string {
    const { spaConfig = {}, pagePattern = {}, tccPagePattern = {} } = this;
    const parsedUrl = Url(url);
    const { pathname, href } = parsedUrl;
    const patternedKey = Object.keys(pagePattern).find((regStr) => new RegExp(regStr).test(href));
    const tccPatternedKey = Object.keys(tccPagePattern).find((regStr) => new RegExp(regStr).test(href));

    if (tccPatternedKey) {
      return tccPagePattern[tccPatternedKey].replace(`${this.btmAId}.`, '');
    }

    if (patternedKey) {
      return pagePattern[patternedKey];
    }

    return spaConfig[pathname] || '';
  }

  /**
   * 返回当前应用的 A点位
   * @notes 该 api 同时暴露给外部 (如热力图，检验工具)使用
   */
  getAId(): string {
    return this.btmAId;
  }

  getBId(): string {
    return this.btmBId;
  }

  /**
   * 获取当前页面的B点位。
   * @notes 该 api 同时暴露给外部 (如热力图，检验工具)使用
   */
  getCurrentPageBId(): string {
    if (this.isMPA) {
      return document.body.getAttribute('data-btm') || 'b0';
    }
    return this.getPageBIdByUrl(window?.location.href) || 'b0';
  }

  /** 帮助业务方获取当前target */
  getCurrentTarget(): HTMLElement | null {
    return this.target;
  }

  /** 获取tcc上页面 URL正则 与 AB 位映射关系 */
  getTccPageRules(
    btmAId: string,
    remoteConfig: Record<string, any>,
    urlMap?: Record<string, string[]>
  ): Record<string, string> {
    let newPagePattern: Record<string, string> = {};
    try {
      this.initBizUrlMap(remoteConfig, urlMap);

      if (!remoteConfig.urlRegexConfKeys || remoteConfig.urlRegexConfKeys.length === 0) {
        return {};
      }
      newPagePattern = getFormatPagePattern(remoteConfig.urlRegexConfKeys, btmAId);
    } catch (error) {
      console.error('fetch TCC config error', error);
    }
    return newPagePattern || {};
  }

  /** 配置上报回调函数 */
  setReportCallback(callback: () => void): void {
    this.reportCallback = callback;
  }

  updateUniqueId(uniqueId: string) {
    this.btmInfo.unique_id = uniqueId;
  }

  getCurrentTimestamp(ts?: number) {
    let baseTs = ts || Date.now();
    if (baseTs <= this.previousTs) {
      baseTs = this.previousTs + 1;
    }
    this.previousTs = baseTs;
    return baseTs;
  }

  /** BTM report */
  btmReport(reportInfo: {
    eventName: string;
    eventParams: Record<string, any>;
    time?: number;
    useBeacon?: boolean;
  }) {
    const { eventName, eventParams, time, useBeacon } = reportInfo;
    eventParams.btm = eventParams.btm.toLowerCase();
    eventParams.btm_page_params = safeStringfy(eventParams.btm_page_params);
    eventParams.btm_pre_unit_params = safeStringfy(eventParams.btm_pre_unit_params);
    eventParams.btm_block_params = safeStringfy(eventParams.btm_block_params);
    eventParams.params = safeStringfy(eventParams.params);

    if (!isValidBTM(eventParams.btm, eventName)) {
      return;
    }

    const curTimestamp = this.getCurrentTimestamp(time);
    let operateTime = this._pre_action_record.ts ? curTimestamp - this._pre_action_record.ts : 0;

    // 针对 biz_succeed 和 biz_fail 场景，operate_time 上报接口请求耗时
    if (
      (eventName === BTMEventType.BizSucceed || eventName === BTMEventType.BizFail) &&
      eventParams.biz_response_time
    ) {
      operateTime = eventParams.biz_response_time;
    }

    const finalEventParams = {
      eventName,
      eventParams: {
        ...eventParams,
        operate_time: operateTime,
        action_btm: eventParams.btm,
        action_btm_pre: this._pre_action_record.action_btm,
        local_ms: curTimestamp
      }
    };

    // 如果是 D 点位点击、C 模块曝光、B 页面曝光，那么需要更新 actionPre 和 timestampPre 值
    if (
      eventName === BTMEventType.Click ||
      eventName === BTMEventType.ModuleShow ||
      eventName === BTMEventType.PageShow
    ) {
      this._pre_action_record = {
        action_btm: eventParams.btm,
        ts: curTimestamp
      };

      // 每次成功的上报，都需要记录到 history 内，但为了避免计算复杂度，需要确保 hitstory 长度不超过 10
      if (this._actionHistory.length >= HISTORY_MAX_LEN) {
        this._actionHistory.shift();
      }
      this._actionHistory.push({
        action_btm: eventParams.btm,
        ts: curTimestamp
      });
    }

    this.trigger('logSent', {
      action_btm: eventParams.btm,
      ts: curTimestamp
    });
    if (eventName === BTMEventType.PageShow && window?.__UNIVERSAL_TRACKER_DEBUG__?.getBtmInfo) {
      window?.__UNIVERSAL_TRACKER_DEBUG__?.getBtmInfo({
        btm: eventParams.btm,
        appId: this.appId
      });
    }

    this.tea?.report(finalEventParams, useBeacon);
  }

  /** 自动化记录接口请求 */
  autoBizStartLog(url: string): IBizAutoLogResponse {
    if (!this._bizUrlBtmMap || !this.isInited) {
      this.logger?.info(`${url} is not registered in bizUrlMap`);
      return { ...EmptyBizLogResponse };
    }

    // 如果 url 没有对应的 btm 点位，那么也不记录
    const btms = this._bizUrlBtmMap.get(url);
    if (!btms || !btms.size) {
      this.logger?.info(`没有找到 url 对应的 btm 点位`);
      return { ...EmptyBizLogResponse };
    }

    const tsStart = Date.now();

    // 由于可能有多个 action_btm，需要去匹配一下，找到时间戳最接近的 action_btm
    const matchedRecords: IActionRecord[] = [];
    btms.forEach((btm) => {
      const matchedRecord = this._getLastMatchRecord(btm, tsStart);
      if (matchedRecord) {
        matchedRecords.push(matchedRecord);
      }
    });

    if (!matchedRecords.length) {
      this.logger?.info('没有找到最近的 action_btm');
      return { ...EmptyBizLogResponse };
    }

    // 选择时间戳最大的那个 record（因为距离接口开始请求时间最近）
    matchedRecords.sort((a, b) => b.ts - a.ts);

    // 找到匹配的 btm 点位
    const matchedBtm = matchedRecords[0].action_btm;

    const fullBtmInfo = this.getFullBtmInfo();

    this.btmReport({
      eventName: BTMEventType.BizStart,
      eventParams: {
        ...fullBtmInfo,
        btm: matchedBtm,
        biz_url: url
      }
    });

    return {
      /**
       * 接口请求失败后的回调函数
       * @param errCode 错误代码
       * @param errMsg 错误信息
       */
      onBizFail: (errCode: number, errMsg: string) => {
        const resTime = Date.now() - tsStart;
        this.btmReport({
          eventName: BTMEventType.BizFail,
          eventParams: {
            ...fullBtmInfo,
            btm: matchedBtm,
            biz_response_time: resTime,
            status_code: errCode,
            fail_msg: errMsg,
            biz_url: url
          }
        });
      },
      /**
       * 接口请求成功的回调函数
       */
      onBizSucceed: () => {
        const resTime = Date.now() - tsStart;
        this.btmReport({
          eventName: BTMEventType.BizSucceed,
          eventParams: {
            ...fullBtmInfo,
            btm: matchedBtm,
            biz_response_time: resTime,
            biz_url: url
          }
        });
      }
    };
  }

  /** 设置 BCM Content */
  setBCMContext(params: Record<string, unknown>) {
    this.BCMContext = params;
  }

  on<K extends keyof EventMap>(eventName: K, handler: EventMap[K]): void {
    if (!this.eventHandlers[eventName]) {
      this.eventHandlers[eventName] = [];
    }
    this.eventHandlers[eventName]?.push(handler as EventHandler);
  }

  // Private method to trigger the event
  private trigger<K extends keyof EventMap>(eventName: K, ...args: Parameters<EventMap[K]>): void {
    const handlers = this.eventHandlers[eventName];
    if (handlers) {
      handlers.forEach((handler) => handler(...args));
    }
  }

  /** 通过 target 和 btm 获取最终的 btm 值 */
  private _getFinalBtm = (target: HTMLElement | null, btm?: string) => {
    let finalBtm = '';

    if (btm) {
      finalBtm = btm;
    }

    if (target) {
      finalBtm = this.getFullBtmInfo(target).btm;
    }

    return this._formatBtm(finalBtm);
  };

  private _formatCdInfo = (fullCode: string) => {
    try {
      // C 点位，如果没有的话默认用 c0
      const btmC = getBtmCode(fullCode, 'c') || 'c0';

      // D 点位由于 index 形式的存在，处理起来比较麻烦
      // case 1: 能找到合法的 d 点位，先精准匹配
      let btmD = getBtmCode(fullCode, 'd');

      // case 2: 如果找不到精准匹配的 d 点位，尝试模糊匹配
      if (!btmD) {
        btmD = getBtmCode(fullCode, 'd', false);
      }

      if (!btmD) {
        const btmArr = fullCode.split('.').map((b) => b.toLowerCase());
        const lastCode = btmArr[btmArr.length - 1];

        // case 3: 模糊匹配也失败的话，开始看最后一个，如果是 a.b.c，或者是 a.b. 这样的情况
        if (isValidCode(lastCode, false)) {
          btmD = 'd0';
        } else {
          // case 4：再匹配不到，认为是 index 模式上报的 d 点位
          btmD = lastCode;
        }
      }

      // 最后兜底
      if (!btmD) {
        btmD = 'd0';
      }
      return `${btmC}.${btmD}`;
    } catch (err) {
      console.error(err);
      return 'c0.d0';
    }
  };

  private _formatBtm = (fullCode: string) => {
    try {
      // A 点位，如果没有的话，用系统 A 点位
      const btmA = getBtmCode(fullCode, 'a') || this.getAId() || 'a0';

      // 系统 B 点位
      const btmB = getBtmCode(fullCode, 'b') || this.getCurrentPageBId() || 'b0';
      const finalBtmB = btmB === 'b0' ? this.btmBId : btmB; // manual init 的 B 位

      const cdInfo = this._formatCdInfo(fullCode);
      return `${btmA}.${finalBtmB}.${cdInfo}`;
    } catch (e) {
      console.error(e);
      return 'a0.b0.c0.d0';
    }
  };

  private shouldReport(url: string, isInit?: boolean) {
    const currBId = this.getPageBIdByUrl(url);
    if (!currBId && isInit) return false;
    if (this.remoteConfig.pageConfig?.disablePageShow) {
      const list = this.remoteConfig.pageConfig?.disablePageShow;
      return !list.includes(currBId);
    }
    return true;
  }

  private initRouteMonitor() {
    this.routeMonitor = new RouteMonitor(this.URL_CHANGE_EVENT_NAME, () => {
      const prevBId = this.btmBId;
      const currBId = this.getPageBIdByUrl(window?.location.href);
      if (prevBId !== currBId) {
        if (this.shouldReport(window?.location.href)) {
          this.pageShowTs = Date.now();
          this.pageInit();
        }
        this.BCMContext = {};
      } else {
        this.logger?.info(`BTM WEB SDK: 相同页面不进行上报。对应 B 位 ${prevBId}`);
      }
    });
  }

  /** 重置BCM信息 */
  private resetBCMParams() {
    this.pageParams = {};
    this.unitParams = null;
    this.resent = false;
  }

  private beforeGetBtmUrl(target: HTMLElement | null) {
    let abp = '';
    let tsp = 0;

    if (target) {
      const { btm } = this.getFullBtmInfo(target);
      // 如果有合法 d 点位
      if (getBtmCode(btm, 'd')) {
        abp = this._formatBtm(btm);
        tsp = Date.now();
      }
    }

    // 这里加上 setTimeout 是为了避免出现 btmJump -> sendBtmClickLog 这样的顺序导致 action_btm 上报不准确
    setTimeout(() => {
      this._saveCachedPreInfo(abp, tsp);
    });
  }

  /** 缓存 pre 信息 */
  private _saveCachedPreInfo(abp?: string, tsp?: number) {
    // 信息缓存在 localStorage 里面，当前一期版本不考虑跨域情况
    if (typeof window?.localStorage?.setItem !== 'function') {
      console.warn('BTM SDK: localStorage.setItem is not supported');
      return;
    }

    if (abp && tsp) {
      universalSetItem(BTM_ACTION_BTM_PRE, abp);
      universalSetItem(BTM_TIMESTAMP_PRE, String(tsp));
      return;
    }

    if (this._pre_action_record.action_btm && this._pre_action_record.ts) {
      universalSetItem(BTM_ACTION_BTM_PRE, this._pre_action_record.action_btm);
      universalSetItem(BTM_TIMESTAMP_PRE, String(this._pre_action_record.ts));
    }
  }

  /** 恢复 pre 信息 */
  private _recoverCachedPreInfo() {
    // 在 spa 场景下，虽然页面 url 变化了，但是 BTM 单例没销毁，不需要从 localStorage 获取
    if (this._pre_action_record.action_btm && this._pre_action_record.ts) {
      return;
    }

    if (typeof window?.localStorage?.getItem !== 'function') {
      console.warn('BTM SDK: localStorage.getItem is not supported！');
      return;
    }

    const abPre = universalGetItem(BTM_ACTION_BTM_PRE) || '';
    const tsPre = Number(universalGetItem(BTM_TIMESTAMP_PRE)) || 0;
    // 如果无缓存结果
    if (!abPre || !tsPre) {
      return;
    }

    // 进行合法性校验，如果 tsPre 和当前时间戳差距比较大，认为是上一次操作缓存的数据
    const curTs = Date.now();
    if (curTs <= tsPre || curTs - tsPre > 30 * 1000) {
      return;
    }

    this._pre_action_record = {
      action_btm: abPre,
      ts: tsPre
    };
  }

  // 查找最近一次 match 到的 btm 点位
  private _getLastMatchRecord = (btm: string, ts: number) => {
    const records = [...this._actionHistory];
    const totelLen = records.length;

    for (let i = 1; i <= totelLen; i++) {
      // 从后往前找
      const record = records[totelLen - i];
      if (matchBtmCode(btm, record.action_btm) && ts - record.ts < BIZ_ACTION_MAX_TS_GAP) {
        return { ...record };
      }
    }
  };

  private initBizUrlMap(tccConfig: Record<string, any>, urlMap?: Record<string, string[]>) {
    let bizUrlMap: Record<string, string[]> = {};
    if (!tccConfig.bizUrlConfig || Object.keys(tccConfig.bizUrlConfig).length === 0) {
      bizUrlMap = { ...urlMap };
    } else {
      bizUrlMap = { ...urlMap, ...tccConfig.bizUrlConfig };
    }

    if (Object.keys(bizUrlMap).length) {
      try {
        this._bizUrlBtmMap = new Map();
        const keys = Object.keys(bizUrlMap);
        for (const btmKey of keys) {
          if (!getBtmCode(btmKey, 'b')) {
            console.warn(`gec btm sdk: ${btmKey} 缺失 b 点位，为无效点位`);
            continue;
          }

          const urls = bizUrlMap[btmKey];

          if (!urls || !urls.length) {
            continue;
          }

          // 格式化一下，以确保格式与后续的 formatedActionBtm 一致
          const formatBtmKey = this._formatBtm(btmKey);

          urls.forEach((url) => {
            if (!this._bizUrlBtmMap?.get(url)) {
              this._bizUrlBtmMap?.set(url, new Set());
            }
            this._bizUrlBtmMap?.get(url)?.add(formatBtmKey);
          });
        }
      } catch (err: any) {
        console.error('Sif Btm Sdk: biz Url 初始化失败', err?.message);
      }
    }
  }
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJtYXBwaW5ncyI6IiIsIm5hbWVzIjpbXSwic291cmNlcyI6W10sInNvdXJjZXNDb250ZW50IjpbXX0=